import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import { storeDefaultInformation } from "../../Store/TemplateStore/template.action";
import {
  editDefaultCIPValues,
  getDefaultCIPValues,
} from "../../network/cipAPI";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import { useTranslation } from "react-i18next";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { SaveIcon } from "../../packages/SVGs";
import CircularLoader from "../../common/styles/components/circularLoader";
import CIPSection from "./CIPSection";

const DefaultCIPInformation = React.forwardRef((props, ref) => {
  const selectedTrain = props.selectedTrain;
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const temperature = useSelector((state) => state.temperature);
  const cips = useSelector((state) => state.cips);
  const defaultCIPState = useSelector((state) => ({
    acid_used_per_cip: state.acid_used_per_cip,
    alkali_used_per_cip: state.alkali_used_per_cip,
    additional_chemical_per_cip: state.additional_chemical_per_cip,
    acid_cip_ph: state.acid_cip_ph,
    alkali_cip_ph: state.alkali_cip_ph,
    cip_solution_ph: state.cip_solution_ph,
    acid_cip_temp: state.acid_cip_temp,
    alkali_cip_temp: state.alkali_cip_temp,
    cip_solution_temp: state.cip_solution_temp,
    acid_cip_duration: state.acid_cip_duration,
    alkali_cip_duration: state.alkali_cip_duration,
    cip_solution_duration: state.cip_solution_duration,
  }));

  const [errors, setErrors] = useState({
    acid_used_per_cip: null,
    alkali_used_per_cip: null,
    additional_chemical_per_cip: null,
    acid_cip_ph: null,
    alkali_cip_ph: null,
    cip_solution_ph: null,
    acid_cip_temp: null,
    alkali_cip_temp: null,
    cip_solution_temp: null,
    acid_cip_duration: null,
    alkali_cip_duration: null,
    cip_solution_duration: null,
  });
  const [noError, setNoError] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const noErrorVal = Object.values(errors).every(
      (error) => error === "" || error === null
    );
    setNoError(noErrorVal);
  }, [errors, props]);

  useEffect(() => {
    if (cips === null) {
      setErrors((prev) => ({
        ...prev,
        acid_cip_temp: null,
        alkali_cip_temp: null,
        cip_solution_temp: null,
      }));
    }
  }, [temperature, cips]);

  const handleInputChange = (field, value, minValue, maxValue) => {
    dispatch(storeDefaultInformation({ [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
    const fieldName = t(`defaultInformationStrings.${field}`).split("(")[0];
    if ((value === "" && minValue !== null) || parseFloat(value) < minValue) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue}`,
      }));
    } else if (
      maxValue !== null &&
      (parseFloat(value) < minValue || parseFloat(value) > maxValue)
    ) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue} ${t(
          "defaultInformationStrings.greaterThan"
        )} ${maxValue}`,
      }));
    }
  };
  // Helper to fetch and restore last saved values
  const fetchAndRestoreDefaultCIP = async () => {
    try {
      const res = await getDefaultCIPValues(selectedTrain);
      const defaultInfo = res?.data;
      if (defaultInfo) {
        dispatch(storeDefaultInformation({ ...defaultInfo }));
      }
    } catch (getError) {
      showErrorNotification(t("defaultInformationStrings.fetchFailed"));
    }
  };

  const handleSave = async () => {
    const payload = { ...defaultCIPState };
    setIsSaving(true);
    try {
      const editRes = await editDefaultCIPValues(selectedTrain, payload);
      if (editRes?.detail || Object.keys(editRes).length === 0) {
        showErrorNotification(t("defaultInformationStrings.saveFailed"));
        await fetchAndRestoreDefaultCIP();
        return;
      }
      // Only update store if save is successful
      dispatch(storeDefaultInformation({ ...editRes }));
      showSuccessNotification(t("defaultInformationStrings.saveSuccess"));
    } catch (editError) {
      showErrorNotification(t("defaultInformationStrings.saveFailed"));
      await fetchAndRestoreDefaultCIP();
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="ro-card-wrapper margin-top-20"
      style={{ position: "relative" }}
    >
      {isSaving && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <div>
        <div className="teal-heading header-border-bottom flex-between">
          {t("defaultInformationStrings.defaultInformation")}
          <WPTertiaryButton
            variant="outlined"
            label={t("defaultInformationStrings.save")}
            onClick={handleSave}
            icon={<SaveIcon width="16" height="16" />}
            iconPlacement="before"
            size="medium"
            disabled={!noError || props.disabled || isSaving}
          />
        </div>
        <div className="ro-component-description">
          {t("defaultInformationStrings.referenceDescription")}
        </div>
        <CIPSection
          defaultCIPKeys={props.defaultCIPKeys}
          errors={errors}
          handleInputChange={handleInputChange}
          disabled={props.disabled}
        />
      </div>
    </div>
  );
});

export default DefaultCIPInformation;
